import { HistoryItem } from './types';
import React from 'react';
import { Box, Flex, Text } from 'rebass';
import FilePreviews from '../File/FilePreviews';
import TimelineStatusComment from './TimelineStatusComment';
import IconLink from '../Icon/IconLink';
import ActivityActionTooltip, {
  getActivityActionTooltipId,
} from '../ActivityFeed/ActivityActionTooltip';
import { format } from 'date-fns';
import Icon from '@/components/Icon/Icon';

export type TimelineItemProps = HistoryItem & {
  noRail?: boolean;
  isInitial?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isFinal?: boolean;
  children?: React.ReactNode;
  footer?: string;
};

const attachmentSourceLabels = {
  MOBILE_CAMERA: 'Taken in mobile app',
  MOBILE_LIBRARY: 'Added from mobile library',
  WEB_UPLOAD: 'Added through web app',
} as const;

function formatMetadataTimestamp(value?: number | null) {
  return value ? format(value, 'MMM d yyyy h:mmaaa') : null;
}

function distanceInMiles(
  latitudeA: number,
  longitudeA: number,
  latitudeB: number,
  longitudeB: number
) {
  const earthRadiusInMiles = 3958.8;
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const latitudeDelta = toRadians(latitudeB - latitudeA);
  const longitudeDelta = toRadians(longitudeB - longitudeA);
  const a =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(latitudeA)) *
      Math.cos(toRadians(latitudeB)) *
      Math.sin(longitudeDelta / 2) ** 2;

  return earthRadiusInMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatLocation({
  latitude,
  longitude,
  jobLatitude,
  jobLongitude,
}: Pick<
  HistoryItem,
  'latitude' | 'longitude' | 'jobLatitude' | 'jobLongitude'
>) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return null;
  }

  if (typeof jobLatitude !== 'number' || typeof jobLongitude !== 'number') {
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }

  const miles = distanceInMiles(latitude, longitude, jobLatitude, jobLongitude);
  if (miles < 0.1) {
    return `${Math.round(miles * 5280).toLocaleString()} ft from job address`;
  }
  return `${miles.toFixed(miles < 10 ? 1 : 0)} mi from job address`;
}

function PhotoAttachmentMetadata({
  source,
  takenAt,
  latitude,
  longitude,
  jobLatitude,
  jobLongitude,
}: {
  source: HistoryItem['attachmentSource'];
  takenAt: HistoryItem['takenAt'];
  latitude: HistoryItem['latitude'];
  longitude: HistoryItem['longitude'];
  jobLatitude: HistoryItem['jobLatitude'];
  jobLongitude: HistoryItem['jobLongitude'];
}) {
  const tooltipId = getActivityActionTooltipId(React.useId());
  const sourceLabel = source ? attachmentSourceLabels[source] : undefined;
  const photoTaken = formatMetadataTimestamp(takenAt);
  const location = formatLocation({
    latitude,
    longitude,
    jobLatitude,
    jobLongitude,
  });
  const rows = [
    sourceLabel ? ['Source', sourceLabel] : null,
    photoTaken ? ['Photo taken', photoTaken] : null,
    location ? ['Location', location] : null,
  ].filter(Boolean) as [string, React.ReactNode][];

  if (!rows.length) return null;

  return (
    <>
      <Box
        as="button"
        type="button"
        data-tooltip-id={tooltipId}
        aria-label="Photo metadata"
        sx={{
          alignItems: 'center',
          bg: 'transparent',
          border: 0,
          color: 'base.gray.500',
          cursor: 'default',
          display: 'inline-flex',
          lineHeight: 0,
          m: 0,
          p: 0,
        }}
      >
        <Icon icon="info" size="12" />
      </Box>
      <ActivityActionTooltip
        id={tooltipId}
        style={{
          zIndex: 1000,
          color: '#fff',
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1.35,
        }}
        render={() => (
          <Box sx={{ minWidth: 190 }}>
            {rows.map(([label, value]) => (
              <Text key={label}>
                {label}: {value}
              </Text>
            ))}
          </Box>
        )}
      />
    </>
  );
}

const TimelineItem = (props: TimelineItemProps) => {
  const {
    noRail = false,
    footer,
    children,
    isInitial = false,
    isFirst = false,
    isLast = false,
    type,
    icon,
    iconColor = '#000',
    title,
    comment,
    adornment,
    isFinal,
    url,
    thumb,
    contentType,
    attachmentSource,
    takenAt,
    latitude,
    longitude,
    jobLatitude,
    jobLongitude,
    showAttachmentMetadata,
  } = props;

  return (
    <Flex flexDirection="column" alignItems="stretch">
      <Box flexGrow="1">
        {type === 'ATTACHMENT' && url && contentType && (
          <FilePreviews
            files={[
              {
                url,
                contentType,
                thumb: thumb ?? null,
              },
            ]}
          />
        )}

        {title && (
          <Flex alignItems="center" sx={{ gap: '2px' }}>
            {!noRail && !!icon && (
              <Box
                width={['10px', '10px', '10px', '20px']}
                display={['none', 'none', 'none', 'flex']}
                alignItems={'center'}
                flexDirection={'column'}
                flexShrink={0}
                className={isFinal ? 'history-rail is-final' : 'history-rail'} // use these to hook onto
              >
                <Flex
                  p="4px"
                  alignItems={'center'}
                  justifyContent={'center'}
                  sx={{
                    background: iconColor,
                    position: 'relative',
                    borderRadius: '50%',
                    '*': {
                      display: ['none', 'none', 'none', 'block'],
                    },
                  }}
                >
                  <Icon icon={icon} color="white" size="10px" />
                </Flex>
              </Box>
            )}
            {url ? (
              <IconLink
                iconAtEnd
                iconSize={3}
                isPrimary
                color="inherit"
                lineHeight="12px"
                fontSize="12px"
                fontWeight="bold"
                href={url}
                m={0}
                p={0}
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  ':hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {title}
              </IconLink>
            ) : (
              <Text
                fontFamily="root"
                lineHeight="12px"
                fontSize="12px"
                fontWeight="bold"
              >
                {title}
              </Text>
            )}
          </Flex>
        )}

        {comment && (
          <TimelineStatusComment mt={title ? 1 : undefined} comment={comment} />
        )}

        {children}

        {/* Meta */}
        {footer && (
          <Flex
            alignSelf="flex-end"
            alignItems="center"
            ml={3}
            mt="3px"
            sx={{ gap: 1 }}
          >
            <Text fontStyle="italic" fontSize="12px" lineHeight="12px">
              {footer}
            </Text>
            {adornment}
            {type === 'ATTACHMENT' && showAttachmentMetadata && (
              <PhotoAttachmentMetadata
                source={attachmentSource}
                takenAt={takenAt}
                latitude={latitude}
                longitude={longitude}
                jobLatitude={jobLatitude}
                jobLongitude={jobLongitude}
              />
            )}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default TimelineItem;
